// Threads, pin numbers and the Open / Resolved split for the review workspace.
//
// The same panel shows comments from three tables — website `review_comments`,
// `document_review_comments` and `video_review_comments` — whose rows differ
// in where they point but agree on the parts a conversation needs: an id, a
// parent, a time, a status. Everything here reads only those.
//
// Pin numbers follow the rule `commentNumbers()` set for websites: a comment's
// number is its position among the roots of its group, oldest first, counting
// every status. A number that moved when something was resolved, or when the
// panel's filter changed, would not be a name for anything.

const time = (row) => {
  const t = Date.parse(row?.created_at || '')
  return Number.isNaN(t) ? 0 : t
}

const byAge = (a, b) => time(a) - time(b) || String(a.id).localeCompare(String(b.id))

const DONE = new Set(['resolved', 'wont_fix'])

/** Resolved, or closed as won't fix. Either way it has left the Open tab. */
export const isResolved = (comment) => DONE.has(String(comment?.status || '').toLowerCase())

export const isRoot = (comment) => !comment?.parent_id

/**
 * Root comment id → its number, per group, oldest first.
 *
 * `groupOf` decides what a number is counted within: a website numbers per
 * page (so pin #2 on the site is #2 here), a document per file and page, a
 * video across the whole version. Replies are never numbered; they are not
 * pins.
 */
export function pinNumbers(comments = [], groupOf = () => 'all') {
  const groups = new Map()
  for (const c of comments || []) {
    if (!c?.id || !isRoot(c)) continue
    const key = groupOf(c) ?? 'all'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(c)
  }
  const out = new Map()
  for (const rows of groups.values()) {
    rows.slice().sort(byAge).forEach((c, i) => out.set(c.id, i + 1))
  }
  return out
}

/**
 * Roots with their replies attached, replies oldest first. A reply whose root
 * is not in the list is dropped rather than promoted — shown alone it answers
 * a question nobody can see.
 */
export function threadComments(comments = []) {
  const rows = (comments || []).filter(c => c?.id)
  const ids = new Set(rows.map(c => c.id))
  const rootOf = new Map()
  const findRoot = (c, depth = 0) => {
    if (!c.parent_id) return c.id
    if (depth > 50) return null
    const parent = rows.find(r => r.id === c.parent_id)
    return parent ? findRoot(parent, depth + 1) : null
  }
  for (const c of rows) rootOf.set(c.id, findRoot(c))

  return rows
    .filter(isRoot)
    .sort(byAge)
    .map(root => ({
      ...root,
      replies: rows
        .filter(c => c.parent_id && ids.has(c.parent_id) && rootOf.get(c.id) === root.id)
        .sort(byAge),
    }))
}

/** How many roots sit on each side of the Open / Resolved tabs. */
export function tabCounts(comments = []) {
  const roots = (comments || []).filter(isRoot)
  const resolved = roots.filter(isResolved).length
  return { open: roots.length - resolved, resolved, all: roots.length }
}

/** The roots for one tab. */
export function rootsForTab(threads = [], tab = 'open') {
  return (threads || []).filter(t => (tab === 'resolved' ? isResolved(t) : !isResolved(t)))
}

/**
 * A freehand drawing, made safe to store and to draw.
 *
 * Strokes are lists of [x, y] in 0–1 of the frame, so a mark made on a laptop
 * lands on the same spot on a phone or in fullscreen. Points outside the frame
 * are clamped, non-numbers dropped, and a stroke of fewer than two points is
 * not a stroke. Null when nothing survives — an empty drawing is no drawing.
 */
export const SHAPE_TYPES = ['pen', 'arrow', 'rect', 'ellipse']
const HEX = /^#[0-9a-f]{3,8}$/i

export function normalizeDrawing(drawing) {
  const clamp = (v) => Math.min(1, Math.max(0, v))
  const pt = (p) => (Array.isArray(p) && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1]))
    ? [Math.round(clamp(Number(p[0])) * 10000) / 10000, Math.round(clamp(Number(p[1])) * 10000) / 10000]
    : null)
  const strokes = (Array.isArray(drawing?.strokes) ? drawing.strokes : [])
    .map(stroke => (Array.isArray(stroke) ? stroke : []).map(pt).filter(Boolean))
    .filter(stroke => stroke.length >= 2)
  const color = typeof drawing?.color === 'string' && HEX.test(drawing.color) ? drawing.color : '#F59E0B'
  // Shapes (arrow, box, circle, pen) each carry their own colour, so one
  // drawing can circle a logo in red and point at a typo in white.
  const shapes = (Array.isArray(drawing?.shapes) ? drawing.shapes : []).map(sh => {
    if (!sh || !SHAPE_TYPES.includes(sh.type)) return null
    const c = typeof sh.color === 'string' && HEX.test(sh.color) ? sh.color : color
    if (sh.type === 'pen') {
      const points = (Array.isArray(sh.points) ? sh.points : []).map(pt).filter(Boolean)
      return points.length >= 2 ? { type: 'pen', color: c, points } : null
    }
    const a = pt(sh.a); const b = pt(sh.b)
    return a && b && (Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) > 0.005) ? { type: sh.type, color: c, a, b } : null
  }).filter(Boolean)
  if (!strokes.length && !shapes.length) return null
  return shapes.length ? { color, strokes, shapes } : { color, strokes }
}

/** Where a drawing's note should sit: the top-left of what was marked. */
export function drawingAnchor(drawing) {
  const d = normalizeDrawing(drawing)
  if (!d) return null
  const pts = [...d.strokes.flat(), ...(d.shapes || []).flatMap(s => (s.type === 'pen' ? s.points : [s.a, s.b]))]
  if (!pts.length) return null
  return [Math.min(...pts.map(p => p[0])), Math.min(...pts.map(p => p[1])), Math.max(...pts.map(p => p[0])), Math.max(...pts.map(p => p[1]))]
}

/** How long a comment stays on screen when it has no range: long enough to read. */
export const MOMENT_SECONDS = 3

/** The stretch of the video a comment belongs to: its range, or a moment after its timecode. */
export function commentWindow(comment) {
  const start = Number(comment?.time_s)
  if (comment?.time_s == null || !Number.isFinite(start)) return null
  const end = comment.end_s != null && Number.isFinite(Number(comment.end_s)) ? Number(comment.end_s) : start + MOMENT_SECONDS
  return [start, Math.max(start, end)]
}

/** An SVG path for one stroke, in a 0–1000 viewBox. */
export function strokePath(stroke = []) {
  return (stroke || [])
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${Math.round(x * 1000)} ${Math.round(y * 1000)}`)
    .join(' ')
}

/**
 * A review comment as a `project_tasks` row — the same shape meeting action
 * items take (lib/meetingTasks). Null without a project: a task has to live
 * somewhere, and a review not tied to one has nowhere to put it.
 */
export function taskFromComment(comment, { projectId, reviewTitle, where, number } = {}) {
  const body = String(comment?.body || '').replace(/\s+/g, ' ').trim()
  if (!body || !projectId) return null
  const title = body.length > 90 ? `${body.slice(0, 87).trimEnd()}…` : body
  const bits = []
  if (reviewTitle) bits.push(`From review: ${reviewTitle}`)
  if (where) bits.push(where)
  if (number) bits.push(`Comment #${number}`)
  if (comment.author_name) bits.push(`Left by ${comment.author_name}`)
  return {
    project_id: projectId,
    title,
    description: [bits.join(' · '), body.length > 90 ? body : ''].filter(Boolean).join('\n\n') || null,
    status: 'todo',
    priority: 'normal',
    completed: false,
  }
}

/** "2h", "3d", "Oct 13" — how long ago a comment was left, for a dense list. */
export function shortAge(iso, now = new Date()) {
  const t = Date.parse(iso || '')
  if (Number.isNaN(t)) return ''
  const mins = Math.max(0, Math.round((now.getTime() - t) / 60000))
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d`
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
